<template>
  <div
    class="deal-card-expanded"
    :class="{
      'deal-card-expanded--closed': isClosed,
    }"
  >
    <div v-if="notificationMessage" :class="notificationClassnames">
      <HdIcon
        :src="icons.checkCircle"
        height="20"
        width="20"
        class="deal-card-expanded__status-icon deal-card-expanded__success-icon"
      />
      <HdIcon
        :src="icons.warning"
        height="20"
        width="20"
        class="deal-card-expanded__status-icon deal-card-expanded__error-icon"
      />
      <p v-html="notificationMessage" />
      <button class="deal-card-expanded__notification-close" @click.prevent="closeNotification">
        <HdIcon :src="icons.close" height="20" width="20" />
      </button>
    </div>
    <div class="deal-card-expanded__title">
      <h3>{{ deal.property.address.formatted_address }}</h3>
      <button @click="closeExpandedDealCard">
        <HdIcon :src="icons.close" height="20" width="20" />
      </button>
    </div>
    <DealCardExpandedTabs
      :sections="sectionsConfig"
      :deal="deal"
      @cardSectionEvent="cardSectionEventHandler"
    />
  </div>
</template>

<script>
import { HdIcon } from 'homeday-blocks';
import { close, checkCircle, warning } from 'homeday-assets';
import DealCardExpandedTabs from '@/components/dashboard/DealCardExpandedTabs.vue';
import { CARD_CONTENT_TYPES } from '@/components/dashboard/CardContent';
import { GETTER, MUTATION } from '@/store/CONSTANTS';
import dashboardCardMixin from '@/mixins/dashboardCardMixin';

const EVENT_TYPES = {
  ADD_NOTE: 'ADD_NOTE',
  ADD_APPOINTMENT: 'ADD_APPOINTMENT',
  ADD_TASK: 'ADD_TASK',
};

export default {
  name: 'DealCardExpanded',
  components: { DealCardExpandedTabs, HdIcon },
  mixins: [dashboardCardMixin],
  data() {
    return {
      isClosed: false,
    };
  },
  computed: {
    icons() {
      return {
        close,
        checkCircle,
        warning,
      };
    },
    deal() {
      return this.$store.getters[GETTER.DASHBOARD.EXPANDED_DEAL];
    },
    dealUid() {
      return this.deal?.uid;
    },
    dealUidInQuery() {
      return this.$route.query?.uid;
    },
    sectionsConfig() {
      const sections = [
        {
          name: 'overview',
          label: this.$t('DASHBOARD.CARD.EXPANDED.SECTION.OVERVIEW'),
          subSections: [
            {
              cards: [
                {
                  title: this.$t('DASHBOARD.CARD.EXPANDED.OVERVIEW.TITLE'),
                  cardType: CARD_CONTENT_TYPES.OVERVIEW,
                },
                {
                  title: this.$t('DASHBOARD.CARD.EXPANDED.CONTACT.TITLE'),
                  cardType: CARD_CONTENT_TYPES.CONTACT,
                },
                {
                  title: this.$t('DASHBOARD.CARD.EXPANDED.PROPERTY_DETAILS.TITLE'),
                  cardType: CARD_CONTENT_TYPES.PROPERTY_DETAILS,
                },
                {
                  title: this.$t('DASHBOARD.CARD.EXPANDED.PRICE.TITLE'),
                  cardType: CARD_CONTENT_TYPES.PRICE_DETAILS,
                },
                {
                  title: this.$t('DASHBOARD.CARD.EXPANDED.MAP.TITLE'),
                  cardType: CARD_CONTENT_TYPES.MAP,
                },
              ],
            },
          ],
          hasStickyBar: true,
        },
        {
          name: 'tasksAndAppointments',
          label: this.$t('DASHBOARD.CARD.EXPANDED.SECTION.TASK_AND_APPOINTMENTS'),
          subSections: [
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.TASKS.TITLE'),
              actionLabel: this.$t('DASHBOARD.CARD.EXPANDED.TASKS.ACTION'),
              actionEvent: EVENT_TYPES.ADD_TASK,
              cards: (this.deal?.open_tasks || []).map((task) => ({
                cardType: CARD_CONTENT_TYPES.TASK,
                cardDynamicData: task,
              })),
            },
            {
              title: this.$tc('DASHBOARD.CARD.MEETING', 2),
              actionLabel: this.$t(
                'DASHBOARD.CARD.EXPANDED.TASK_AND_APPOINTMENTS.APPOINTMENT.BUTTON',
              ),
              actionEvent: EVENT_TYPES.ADD_APPOINTMENT,
              cards: (this.deal?.upcoming_appointments || []).map((appointment) => ({
                cardType: CARD_CONTENT_TYPES.APPOINTMENT,
                cardDynamicData: appointment,
              })),
            },
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.TASK_AND_APPOINTMENTS.CALLBACK_REQUESTS'),
              cards: (this.deal?.callback_requests || []).map((callbackRequest) => ({
                cardType: CARD_CONTENT_TYPES.CALLBACK_REQUEST,
                cardDynamicData: callbackRequest,
              })),
            },
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.TASK_AND_APPOINTMENTS.STATUS_PROMPT'),
              cards: (this.deal?.status_prompts || []).map((statusPrompt) => ({
                cardType: CARD_CONTENT_TYPES.STATUS_PROMPT,
                cardDynamicData: statusPrompt,
              })),
            },
          ],
        },
        {
          name: 'notes',
          label: this.$t('DASHBOARD.CARD.EXPANDED.SECTION.NOTES'),
          subSections: [
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.NOTES.TITLE'),
              actionLabel: this.$t('DASHBOARD.CARD.EXPANDED.NOTES.BUTTON'),
              actionEvent: EVENT_TYPES.ADD_NOTE,
              cards: (this.deal?.notes || []).map((note) => {
                const category = Object.keys(note)[0];
                return {
                  cardType: CARD_CONTENT_TYPES.NOTE,
                  cardDynamicData: {
                    ...note[category],
                    category,
                  },
                };
              }),
            },
          ],
        },
      ];

      if (this.deal?.status_group === 'find_and_select_buyer') {
        sections
          .at(0)
          .subSections.at(0)
          .cards.splice(
            2,
            0,
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.PORTAL.TITLE'),
              cardType: CARD_CONTENT_TYPES.PORTALS,
            },
            {
              title: this.$t('DASHBOARD.CARD.EXPANDED.MARKETING.TITLE'),
              cardType: CARD_CONTENT_TYPES.MARKETING,
            },
          );
      }

      return sections;
    },
    notificationMessage() {
      return this.$store.state.dashboard.notification.message;
    },
    notificationClassnames() {
      const notificationType = this.$store.state.dashboard.notification.type;
      const classname = 'deal-card-expanded__notification';
      const status = `deal-card-expanded__notification--${notificationType}`;
      return [classname, status];
    },
  },
  watch: {
    notificationMessage(newValue) {
      const { autoClose } = this.$store.state.dashboard.notification;
      if (newValue && autoClose) {
        setTimeout(this.closeNotification, 3000);
      }
    },
  },
  created() {
    this.addDealUidToQuery();

    const onEscape = ({ keyCode }) => {
      if (keyCode === 27 && !this.isClosed) this.closeExpandedDealCard();
    };

    window.addEventListener('keydown', onEscape);

    this.$once('hook:destroyed', () => {
      window.removeEventListener('keydown', onEscape);
    });
  },
  destroyed() {
    this.removeDealUidFromQuery();
  },
  methods: {
    closeExpandedDealCard() {
      this.isClosed = true;
      this.removeDealUidFromQuery();
      setTimeout(() => this.$store.commit(MUTATION.DASHBOARD.SET_EXPANDED_DEAL, null), 300);
    },
    cardSectionEventHandler(eventName) {
      if (eventName === EVENT_TYPES.ADD_NOTE) {
        this.$store.commit(MUTATION.DASHBOARD.TOGGLE_CREATE_NOTE_MODAL_VISIBILITY, this.deal.uid);
      } else if (eventName === EVENT_TYPES.ADD_APPOINTMENT) {
        this.actionHandler(this.ACTIONS.CREATE_APPOINTMENT);
      } else if (eventName === EVENT_TYPES.ADD_TASK) {
        this.actionHandler(this.ACTIONS.CREATE_TASK);
      }
    },
    async addDealUidToQuery() {
      if (this.dealUidInQuery) return;
      await this.$router
        .replace({
          query: {
            ...this.$route.query,
            uid: this.dealUid,
          },
        })
        .catch(() => ({}));
    },
    async removeDealUidFromQuery() {
      if (!this.dealUidInQuery) return;
      // eslint-disable-next-line no-unused-vars
      const { uid: _, ...query } = this.$route.query;
      await this.$router.replace({ query }).catch(() => ({}));
    },
    closeNotification() {
      return this.$store.commit(MUTATION.DASHBOARD.UNSET_NOTIFICATION);
    },
  },
};
</script>
<style lang="scss" scoped>
@import '@/styles/mixins.scss';

.deal-card-expanded {
  $root: &;
  animation: slideUp ($time-s * 2) forwards;
  background-color: getShade($primary-color, 90);
  border-radius: $sp-s $sp-s 0 0;
  color: $primary-bg;
  width: 100%;
  height: 100%;
  opacity: 1;
  overflow: hidden;
  position: fixed;
  top: $sp-xxs;
  left: 0;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);
  z-index: 30;
  -webkit-overflow-scrolling: touch;

  @include for('tablet') {
    top: $sp-m;
  }

  &--closed {
    animation: slideDown ($time-s * 2) forwards;
  }

  &__title,
  &__notification {
    background-color: getShade($primary-color, 90);
    display: flex;
    justify-content: space-between;
    padding: $sp-m;
    position: sticky;
    top: 0;
    z-index: 99;

    @include for('tablet') {
      @include font('DS-300');
    }
  }

  &__title {
    @include for('tablet') {
      position: static;
    }
  }

  &__notification {
    position: absolute;
    z-index: 100;
    width: 100%;
    min-height: 104px;
    justify-content: flex-start;
    column-gap: $sp-s;

    #{$root}__status-icon {
      display: none;
    }

    &--success {
      background-color: $success-color;

      #{$root}__success-icon {
        display: block;
      }
    }

    &--error {
      background-color: $error-color;

      #{$root}__error-icon {
        display: block;
      }
    }

    &-close {
      margin-left: auto;
      margin-top: 0;
    }

    ::v-deep svg {
      min-height: $sp-m + $sp-s;
      min-width: $sp-m + $sp-s;

      path {
        fill: $primary-bg;
      }
    }

    @include for('desktop-wide') {
      min-height: 64px;
    }
  }

  button {
    background-color: transparent;
    border: none;
    cursor: pointer;
    display: flex;
    margin-top: $sp-xs;
    padding: 0;

    ::v-deep svg {
      min-height: $sp-m + $sp-s;
      min-width: $sp-m + $sp-s;

      path {
        fill: $primary-bg;
      }
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(0);
    }
    to {
      transform: translateY(100%);
    }
  }
}
</style>
